import { useState } from 'react'
import { Button, Form, Input, Select, Tag, InputNumber, DatePicker, App } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { shipmentAdvicesApi } from '@/features/procurement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import type { ShipmentAdvice } from '@/shared/types/procurement'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'

const STATUS_COLORS: Record<string, string> = { IN_TRANSIT: 'blue', ARRIVED: 'green', CANCELLED: 'red' }

export default function ShipmentAdvicePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['shipment-advices', plantFilter],
    queryFn: () => shipmentAdvicesApi.list({ plant_id: plantFilter }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })

  const plantOptions = plantsData?.items.map(p => ({ label: p.name, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const payload = {
        ...values,
        expected_arrival: values.expected_arrival
          ? dayjs(values.expected_arrival as string).toISOString()
          : undefined,
      }
      return shipmentAdvicesApi.create(payload)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shipment-advices'] }); message.success('Shipment Advice created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Create failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      shipmentAdvicesApi.update(id, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shipment-advices'] }); message.success('Status updated') },
    onError: () => message.error('Update failed'),
  })

  const columns = [
    { title: 'SA No', dataIndex: 'sa_no', key: 'sa_no', width: 120 },
    { title: 'PO No', dataIndex: 'po_no', key: 'po_no', width: 120 },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name' },
    { title: 'Vehicle', dataIndex: 'vehicle_no', key: 'vehicle_no', width: 100 },
    { title: 'Dispatched Kg', dataIndex: 'quantity_dispatched_kg', key: 'quantity_dispatched_kg', width: 120 },
    { title: 'LR No', dataIndex: 'lr_no', key: 'lr_no', width: 100 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag>,
    },
    {
      title: '', key: 'arrive', width: 100,
      render: (_: unknown, r: ShipmentAdvice) =>
        r.status === 'IN_TRANSIT'
          ? <a onClick={() => updateMutation.mutate({ id: r.id, status: 'ARRIVED' })}>Mark Arrived</a>
          : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Shipment Advices"
        subtitle="Track supplier dispatches in transit"
        extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setDrawerOpen(true) }}>New SA</Button>}
      />

      <div style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 180 }} />
      </div>

      <DataTable rowKey="id" columns={columns} dataSource={data} loading={isLoading} />

      <FormDrawer
        title="New Shipment Advice"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending}
        form={form}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Form.Item name="supplier_id" label="Supplier" rules={[{ required: true }]}>
          <Select options={supplierOptions} showSearch optionFilterProp="label" />
        </Form.Item>
        <Form.Item name="vehicle_no" label="Vehicle No">
          <Input />
        </Form.Item>
        <Form.Item name="driver_name" label="Driver Name">
          <Input />
        </Form.Item>
        <Form.Item name="driver_phone" label="Driver Phone">
          <Input />
        </Form.Item>
        <Form.Item name="quantity_dispatched_kg" label="Quantity Dispatched (Kg)" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0.001} />
        </Form.Item>
        <Form.Item name="lr_no" label="LR No">
          <Input />
        </Form.Item>
        <Form.Item name="expected_arrival" label="Expected Arrival">
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
